<template>
    <div>
        <div v-if="loaded">
            <!-- Row top buttons -->
            <div class="row q-mb-lg">
                <div class="col-6" style="justify-content: flex-start">
                    <q-btn
                        class="capitalize ctrl-button"
                        color="grey-5"
                        :label="`${$store.state.stringsStore.strings.action.toOverview}`"
                        @click="$router.push('/planning')"
                        icon="keyboard_backspace"
                    />
                </div>
                <div class="col-6">
                    <q-btn
                        ref="save"
                        class="capitalize ctrl-button right q-mr-sm"
                        :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
                        :label="
                            readOnly
                                ? getString('action.edit')
                                : getString('action.save')
                        "
                        @click="saveChanges"
                        :icon-right="readOnly ? 'edit' : 'save'"
                    />
                    <q-btn
                        v-if="$route.params.id"
                        class="q-mr-md capitalize ctrl-button right"
                        icon-right="delete"
                        color="negative capitalize"
                        :label="$store.state.stringsStore.strings.action.delete"
                        align="right"
                        @click="removePlanning()"
                    />
                </div>
            </div>

            <!-- Columns -->
            <div class="row gutter-sm q-mb-lg">
                <!-- Left -->
                <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
                    <div class="row q-mb-sm">
                        <div class="full-width">
                            <p class="card-title">
                                {{ getString("action.details") }}
                            </p>

                            <div class="card-block">
                                <div class="row">
                                    <div class="col-md-12">
                                        <q-field class="q-mb-md"
                                          :error="
                                              $v.data.course.id.$dirty &&
                                              !$v.data.course.id.required
                                          ">
                                            <q-search
                                                v-model="data.course.title"
                                                clearable
                                                @clear="data.course.id = null"
                                                :disable="readOnly"
                                                :float-label="
                                                    `${getString(
                                                        'module.planning.details.course'
                                                    )} *`
                                                "
                                            >
                                                <q-autocomplete
                                                    value-field="label"
                                                    :max-results="25"
                                                    :min-characters="2"
                                                    @search="
                                                        autocompleteCourse
                                                    "
                                                    @selected="selectCourse"
                                                />
                                            </q-search>
                                        </q-field>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12 q-pr-md">
                                        <q-datetime
                                            format24h
                                            :disable="readOnly"
                                            type="datetime"
                                            class="full-width q-mb-md"
                                            :error="
                                                $v.data.datetime_start.$dirty &&
                                                !$v.data.datetime_start.required
                                            "
                                            :float-label="
                                                `${getString(
                                                    'module.planning.details.date.course'
                                                )} *`
                                            "
                                            v-model="data.datetime_start"
                                        />
                                    </div>
                                </div>

                                <!-- Instructors -->
                                <div class="row">
                                    <div class="col-md-12">
                                        <chips-autocomplete
                                            :key="instructorKey"
                                            class="q-pb-md"
                                            :disable="readOnly"
                                            :selection="data.instructors"
                                            :name="`${getString('course.instructors')} *`"
                                            :labelTerm="'name'"
                                            :endpoint="'course/instructor'"
                                            :resultLabel="'instructors'"
                                            customPost
                                            :promptNewMessage="getString(null, 'Add extrenal instructor', 'Externe docent toevoegen')"
                                            :error="
                                                $v.data.instructors.$dirty &&
                                                !$v.data.instructors.required
                                            "
                                            :errorLabel="getString(null, 'Select at least one instructor', 'Selecteer minimaal één instructeur')"
                                            @add="promptCustomInstructor"
                                            @removed="removeExternalInstructor"
                                        />
                                    </div>
                                </div>

                                <div class="row">
                                  <q-field
                                    class="q-mb-md full-width"
                                    >
                                      <q-search
                                          :disable="data.course.online || readOnly"
                                          v-model="data.location.name"
                                          clearable
                                          :float-label="`${getString('block.type.button.location')}${data.course.online ? ' (Online)' : ''}`"
                                      >
                                          <q-autocomplete
                                              value-field="label"
                                              :max-results="25"
                                              :min-characters="2"
                                              @search="
                                                  autocompleteLocation
                                              "
                                              @selected="selectLocation"
                                          />
                                      </q-search>
                                  </q-field>
                                </div>

                                <div class="row">
                                    <q-input
                                        type="number"
                                        class="full-width q-mb-md"
                                        :disable="readOnly"
                                        :float-label="
                                            getString(
                                                'module.planning.details.max_participants'
                                            )
                                        "
                                        v-model="data.max_participants"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right -->
                <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">

                        <!-- Publication block -->
                        <div class="row q-mb-sm">
                            <div class="full-width">
                                <p class="card-title">{{ getString('course.registration.header') }}</p>
                                <div class="card-block scroll">
                                    <div class="row">
                                        <div class="col q-mr-sm">
                                        <q-select
                                            :disable="readOnly"
                                            :readonly="readOnly"
                                            class="full-width q-mb-md"
                                            placeholder="Publish"
                                            :float-label="`${getString('course.registration.status')} *`"
                                            v-model="data.status"
                                            :error="
                                                $v.data.status.$dirty &&
                                                !$v.data.status.required
                                            "
                                            :options="publicationOptions"
                                            @input="publicationChange()"
                                        />
                                        </div>
                                        <div class="col q-ml-sm">
                                        <!-- Publication date -->

                                    <q-field
                                        class="full-width"
                                        :error="
                                            $v.data.datetime_registration.$dirty &&
                                            !$v.data.datetime_registration.required
                                        "
                                        :errorLabel="
                                            $store.state.stringsStore
                                                .language === 'nl'
                                                ? 'Verplicht tenzij opslaan als concept'
                                                : 'Required unless saving as concept'
                                        ">
                                          <q-datetime
                                              format24h
                                              class="full-width q-mb-md"
                                              :disable="this.data.status !== 'published' || readOnly"
                                              :readonly="this.data.status !== 'published'"
                                              :float-label="getString('course.registration.datetime')"
                                              v-model="data.datetime_registration"
                                              type="datetime"
                                          />
                                      </q-field>
                                        </div>
                                    </div>

                                    <div class="row q-mt-md q-mb-md">
                                        <q-field>
                                            <q-toggle
                                                :disable="readOnly"
                                                v-model="data.full"
                                                color="secondary"
                                                :label="getString('module.planning.details.full')"
                                            />
                                        </q-field>
                                    </div>
                                </div>
                            </div>
                        </div>

                </div>
            </div>
        </div>
        <div class="loader" v-else>
            <q-spinner color="grey-5" :size="80" />
        </div>

        <!-- Modal to select templates -->
        <q-modal
          no-esc-dismiss
          no-backdrop-dismiss
          v-model="showCustomInstructorModal">
          <div class="q-ma-lg">
            <div class="row">
              <p>{{ getString(null, 'Add extrenal instructor', 'Externe docent toevoegen')}}</p>
            </div>
          <q-field
            :error="$v.newCustomInstructor.firstname.$invalid"
            :error-label="getString(null, 'Firstname is required', 'Voornaam is vereist')">
            <q-input
              :float-label="getString('firstname')"
              v-model="newCustomInstructor.firstname"
            ></q-input>
          </q-field>
          <q-field
            :error="$v.newCustomInstructor.lastname.$invalid"
            :error-label="getString(null, 'Lastname is required', 'Achternaam is vereist')">
            <q-input
              :float-label="getString('surname')"
              v-model="newCustomInstructor.lastname"
            ></q-input>
          </q-field>
          <q-field
            :error="$v.newCustomInstructor.email.$invalid"
            :error-label="getString(null, 'A valid email is required', 'Een geldig e-mailadres is vereist')">
            <q-input
              :float-label="getString('email')"
              :error="$v.newCustomInstructor.email.$dirty && $v.newCustomInstructor.email.$invalid"
              v-model="newCustomInstructor.email"
            ></q-input>
          </q-field>
            <div class="row q-mt-md" style="display: flex; justify-content: flex-end;">
              <q-btn
                  class="capitalize ctrl-button right q-mr-sm"
                  color="negative"
                  label="Cancel"
                  @click="showCustomInstructorModal = false"
              />
              <q-btn
                  class="capitalize ctrl-button right q-mr-sm"
                  color="ctrl-turquoise"
                  label="OK"
                  :disable="$v.newCustomInstructor.$anyError"
                  @click="addCustomInstructor"
              />
            </div>
          </div>
        </q-modal>
    </div>
</template>

<script>
import { required, requiredIf, email, minLength } from 'vuelidate/lib/validators';
import moment from 'moment';
import EventBus from '@/event-bus';

// Mixins
import saveChanges from '@/mixins/detailViews/saveChanges';
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import getString from '@/helpers/GetStrings';
import college from '@/plugins/api/college/college';


import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';

export default {
  validations: {
    newCustomInstructor: {
      email: {
        email,
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
      firstname: {
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
      lastname: {
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
    },
    data: {
      course: {
        id: {
          required,
        },
      },
      datetime_start: {
        required,
      },
      status: {
        required,
      },
      datetime_registration: {
        required: requiredIf(function () {
          return this.data.status !== 'draft';
        }),
      },
      instructors: {
        required,
        minLength: minLength(1),
      },
    },
  },
  mixins: [
    fetchDetail,
    saveChanges,
    college,
  ],
  components: {
    ChipsAutocomplete,
  },
  data() {
    return {
      instructorKey: 0,
      loaded: false,
      readOnly: true,
      // Name of the module defined in user rights. eg. blog for blog.edit, blog.add etc..
      permission: 'planning',
      // Where data is fetched from
      endpoint: 'planning',
      // The object key from the response object
      responseData: {
        single: 'planning',
        multiple: 'plannings',
      },
      // Used for navigation on the front-end (matching the router)
      module: 'planning',
      imageModal: false,
      containsErrors: true,
      data: {
        course: {
          id: null,
          title: '',
          online: false,
        },
        location: {
          name: '',
          id: null,
        },
        instructors: [],
        active: false,
        full: false,
        max_participants: null,
        datetime_registration: null,
        datetime_start: null,
      },
      publicationOptions: [
        {
          label: getString('publication.option.draft'),
          value: 'draft',
        },
        {
          label: getString('publication.option.select'),
          value: 'published',
        },
      ],
    };
  },

  methods: {
    getString,

    /**
     * Search for course
     */
    autocompleteCourse(terms, done) {
      // Reset course on input
      this.axios.get(`course?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.courses.map(item => ({
          label: item.title,
          id: item.id,
          online: item.sort === 'webinar',
          value: item.id,
          location: item.location,
        }));
        done(suggestions);
      });
    },

    selectCourse(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      this.data.course.title = found.label;
      this.data.course.id = found.id;
      this.data.course.online = found.online;

      this.fetchDefaultInstructors(found.id);

      // If the course is online, remove the location
      if (!found.online) {
        this.fillDefaultLocation(found.location);
      } else {
        this.data.location.name = null;
        this.data.location.id = null;
      }
    },

    fetchDefaultInstructors(courseId) {
      this.axios.get(`/course/${courseId}`).then((res) => {
        const mergedInstructors = res.data.course.external_instructors.map(instructor => ({
          name: `${instructor.firstname} ${instructor.lastname}`,
          value: 'external',
          id: instructor.id,
        }));
        const instructors = [...res.data.course.instructors, ...mergedInstructors];
        this.$nextTick(() => {
          this.data.instructors = [...this.data.instructors, ...instructors];
          this.instructorKey += 1;
        });
        this.customInstructors = res.data.course.external_instructors;
      })
        .catch((err) => {
          console.warn(err);
        });
    },

    // Prefil location field with default course location
    fillDefaultLocation(location) {
      // Ignore if location is already selected
      if (this.data.location.name) return;
      this.selectLocation({ label: location.name, id: location.id });
    },

    /**
     * Search for location
     */
    autocompleteLocation(terms, done) {
      // Reset location on input
      this.axios.get(`location?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.locations.map(item => ({
          label: item.name,
          id: item.id,
          value: item.id,
        }));
        done(suggestions);
      });
    },

    selectLocation(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      this.data.location.name = found.label;
      this.data.location.id = found.id;
    },

    requestParams() {
      const params = {
        course: this.data.course.id,
        full: this.data.full ? 1 : 0,
        status: this.data.status,
        max_participants: this.data.max_participants,
        instructor: this.data.instructors.filter(instructor => instructor.value !== 'external').map(instructor => instructor.id),
        datetime_start: this.data.datetime_start ? moment(this.data.datetime_start).utc().format('YYYY-MM-DD HH:mm:ss') : null,
        datetime_registration: this.data.datetime_registration ? moment(this.data.datetime_registration).utc().format('YYYY-MM-DD HH:mm:ss') : null,
      };

      // Manually add custom instructor data to keep array indices
      this.customInstructors.forEach((instructor, index) => {
        params[`external_instructor[${index}][firstname]`] = instructor.firstname;
        params[`external_instructor[${index}][lastname]`] = instructor.lastname;
        params[`external_instructor[${index}][email]`] = instructor.email;
      });

      if (params.status === 'draft') {
        params.datetime_registration = null;
      }

      if (!this.data.course.online && this.data.location) {
        params.location_id = this.data.location.id;
      }

      for (const [key, value] of Object.entries(params)) {
        // If the value is not null, undefined or empty, remove it from the params.
        if (value === null || value === undefined) delete params[key];
      }

      return params;
    },

    removePlanning() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: this.getString('module.library.delete.title.single'),
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.deletePlanning(this.$route.params.id).then(() => {
          this.$q.notify({
            message: this.getString(null, 'Planning successfully removed', 'Planning succesvol verwijderd'),
            timeout: 2000,
            type: 'positive',
            position: 'top-right',
          });
          this.$router.push('/planning');
        });
      }).catch(() => {
        console.warn('Not removed');
      });
    },
  },

  async mounted() {
    EventBus.$on('chips-autocomplete:instructors', (selection) => {
      this.instructorKey += 0;
      this.$set(this.data, 'instructors', selection);
    });

    if (this.$route.params.id) {
      this.axios.get(`planning/${this.$route.params.id}`).then((res) => {
        this.data = res.data.planning;
        const mergedInstructors = this.data.external_instructors.map(instructor => ({
          name: `${instructor.firstname} ${instructor.lastname}`,
          value: 'external',
          id: instructor.id,
        }));
        this.data.instructors = [...this.data.instructors, ...mergedInstructors];
        this.customInstructors = this.data.external_instructors;
        this.data.location = res.data.planning.location || { name: '', id: null };
        this.loaded = true;
      });
    } else {
      this.loaded = true;
      this.readOnly = false;
    }
  },
};
</script>

<style lang="stylus" scoped>
.full-height
  height: 100%
</style>
